import { useEffect, useState, useCallback, useRef } from 'react';
import { placeholderFor } from '../../utils/listingPlaceholder';
import { Link } from 'react-router-dom';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { fetchPublicProfiles } from '../../utils/publicProfilesBatch';
import { perfMark } from '../../utils/perfMarks';
import { db } from '../../firebase';
import { getInitiatorPedalIds } from '../../utils/tradeUtils';
import ListingThumb from '../ListingThumb';
import './ActiveTradesPurchasesStrip.css';

const TRADE_STATUSES = ['accepted', 'inProgress', 'payment_completed'];
const PURCHASE_STATUSES = ['accepted', 'inProgress', 'payment_completed', 'paid'];

/** Offers that should never appear on the dashboard strip */
const TERMINAL_STATUSES = new Set(['complete', 'completed', 'cancelled', 'declined', 'rejected', 'expired']);

const formatPedalTitle = (pedal) => {
  if (!pedal) return 'Pedal';
  const m = (pedal.make || '').trim();
  const o = (pedal.model || '').trim();
  if (m && o) return `${m} ${o}`;
  return m || o || 'Pedal';
};

const AWAITING_PARTNER = 'Awaiting partner';

/** True when the trade no longer belongs on the strip (strip hides when true). */
const tradeOfferFullyDone = (trade) => {
  const transferSuccessful = trade.transferStatus?.status === 'success';
  const userA = {
    paid: trade.paymentStatus?.fromUserPaid || false,
    payoutReceived: transferSuccessful,
    photoUploaded: (trade.shippingStatus?.fromUserPhotos?.length > 0),
    ready: !!trade.shippingStatus?.fromUserReady,
    label: !!trade.shippingStatus?.fromUser?.labelPurchased,
    delivered: trade.shippingStatus?.fromUser?.delivered || false,
  };
  const userB = {
    paid: trade.paymentStatus?.toUserPaid || false,
    payoutReceived: transferSuccessful,
    photoUploaded: (trade.shippingStatus?.toUserPhotos?.length > 0),
    ready: !!trade.shippingStatus?.toUserReady,
    label: !!trade.shippingStatus?.toUser?.labelPurchased,
    delivered: trade.shippingStatus?.toUser?.delivered || false,
  };

  const aPaid = userA.paid || userA.payoutReceived;
  const bPaid = userB.paid || userB.payoutReceived;
  if (!aPaid || !bPaid) return false;
  if (!userA.photoUploaded || !userB.photoUploaded) return false;
  if (!userA.ready || !userB.ready) return false;
  if (!userA.label || !userB.label) return false;
  if (!userA.delivered || !userB.delivered) return false;
  if (!trade.fromUserFeedback || !trade.toUserFeedback) return false;
  return true;
};

/** True when the purchase no longer belongs on the strip. */
const purchaseOfferFullyDone = (purchase) => {
  const pay = purchase.paymentStatus || {};
  const ship = purchase.shippingStatus || {};
  if (!pay.buyerPaid || !pay.sellerPaid) return false;
  if (!ship.confirmationPhoto) return false;
  if (ship.sellerReady === false) return false;
  if (!ship.labelCreated && !ship.labelPurchased) return false;
  if (!ship.delivered) return false;
  if (!purchase.buyerFeedback || !purchase.sellerFeedback) return false;
  return true;
};

/**
 * Short CTA for the viewer: their next action, or "Awaiting partner" when they’re caught up
 * but the other party still owes a step. After both have shipping labels, uses "In transit"
 * until both deliveries are confirmed (then "Feedback" / "Awaiting partner").
 */
const getTradeDashboardCta = (trade, uid) => {
  if (tradeOfferFullyDone(trade)) return null;

  const p = trade.paymentStatus || {};
  const s = trade.shippingStatus || {};
  const transferSuccessful = trade.transferStatus?.status === 'success';
  const isFrom = trade.fromUserId === uid;

  const myPaid = isFrom ? !!p.fromUserPaid : !!p.toUserPaid;
  const theirPaid = isFrom ? !!p.toUserPaid : !!p.fromUserPaid;
  const myPaidOk = myPaid || transferSuccessful;
  const theirPaidOk = theirPaid || transferSuccessful;

  if (!myPaidOk) return 'Payment';
  if (!theirPaidOk) return AWAITING_PARTNER;

  const myPhotos = isFrom ? (s.fromUserPhotos?.length > 0) : (s.toUserPhotos?.length > 0);
  const theirPhotos = isFrom ? (s.toUserPhotos?.length > 0) : (s.fromUserPhotos?.length > 0);
  if (!myPhotos) return 'Upload photo';
  if (!theirPhotos) return AWAITING_PARTNER;

  const myReady = isFrom ? !!s.fromUserReady : !!s.toUserReady;
  const theirReady = isFrom ? !!s.toUserReady : !!s.fromUserReady;
  if (!myReady) return 'Ready to ship';
  if (!theirReady) return AWAITING_PARTNER;

  const myLabel = isFrom ? !!s.fromUser?.labelPurchased : !!s.toUser?.labelPurchased;
  const theirLabel = isFrom ? !!s.toUser?.labelPurchased : !!s.fromUser?.labelPurchased;
  if (!myLabel) return 'Shipping';
  if (!theirLabel) return AWAITING_PARTNER;

  const myDel = isFrom ? !!s.fromUser?.delivered : !!s.toUser?.delivered;
  const theirDel = isFrom ? !!s.toUser?.delivered : !!s.fromUser?.delivered;
  if (!myDel && !theirDel) return 'In transit';
  if (myDel && !theirDel) return AWAITING_PARTNER;
  if (!myDel && theirDel) return 'In transit';

  const myFb = isFrom ? !!trade.fromUserFeedback : !!trade.toUserFeedback;
  const theirFb = isFrom ? !!trade.toUserFeedback : !!trade.fromUserFeedback;
  if (!myFb) return 'Feedback';
  if (!theirFb) return AWAITING_PARTNER;
  return null;
};

/**
 * Short CTA for purchase: buyer / seller see their action or "Awaiting partner".
 * "In transit" after the label exists until delivered.
 */
const getPurchaseDashboardCta = (purchase, uid) => {
  if (purchaseOfferFullyDone(purchase)) return null;

  const pay = purchase.paymentStatus || {};
  const ship = purchase.shippingStatus || {};
  const isBuyer = purchase.initiatorId === uid;

  if (!pay.buyerPaid) return isBuyer ? 'Payment' : AWAITING_PARTNER;
  if (!pay.sellerPaid) return isBuyer ? AWAITING_PARTNER : 'Payment';

  if (!ship.confirmationPhoto) return isBuyer ? AWAITING_PARTNER : 'Upload photo';

  if (ship.sellerReady === false) return isBuyer ? AWAITING_PARTNER : 'Ready to ship';

  if (!ship.labelCreated && !ship.labelPurchased) return isBuyer ? AWAITING_PARTNER : 'Shipping';

  if (!ship.delivered) return 'In transit';

  const myFb = isBuyer ? !!purchase.buyerFeedback : !!purchase.sellerFeedback;
  const theirFb = isBuyer ? !!purchase.sellerFeedback : !!purchase.buyerFeedback;
  if (!myFb) return 'Feedback';
  if (!theirFb) return AWAITING_PARTNER;
  return null;
};

const enrichTrade = async (trade, uid) => {
  let initiatorPedals = [];
  let responderPedal = null;

  if (trade.pedalSnapshots?.initiatorPedal && trade.pedalSnapshots?.responderPedal) {
    initiatorPedals = trade.pedalSnapshots.initiatorPedals?.length
      ? trade.pedalSnapshots.initiatorPedals
      : [trade.pedalSnapshots.initiatorPedal];
    responderPedal = trade.pedalSnapshots.responderPedal;
  } else {
    const ids = getInitiatorPedalIds(trade);
    const initiatorSnaps = await Promise.all(ids.map((pid) => getDoc(doc(db, 'listings', pid))));
    initiatorPedals = initiatorSnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
    if (trade.responderPedalId) {
      const rs = await getDoc(doc(db, 'listings', trade.responderPedalId));
      responderPedal = rs.exists() ? { id: rs.id, ...rs.data() } : null;
    }
  }

  const isInitiator = uid === trade.fromUserId;
  const theirPedal = isInitiator ? responderPedal : initiatorPedals[0];
  const otherUid = isInitiator ? trade.toUserId : trade.fromUserId;

  return {
    key: `trade-${trade.id}`,
    kind: 'trade',
    id: trade.id,
    href: `/trade-progress/${trade.id}`,
    image: theirPedal?.images?.top || theirPedal?.images?.upper || (theirPedal ? placeholderFor(theirPedal).url : null),
    title: formatPedalTitle(theirPedal),
    cta: getTradeDashboardCta(trade, uid),
    otherUid,
  };
};

const enrichPurchase = async (purchase, uid) => {
  let target = {};
  if (purchase.pedalSnapshot?.targetPedal) {
    target = purchase.pedalSnapshot.targetPedal;
  } else if (purchase.targetPedalId) {
    const snap = await getDoc(doc(db, 'listings', purchase.targetPedalId));
    target = snap.exists() ? { id: snap.id, ...snap.data() } : {};
  }
  const isBuyer = uid === purchase.initiatorId;
  const otherUid = isBuyer ? purchase.responderId : purchase.initiatorId;

  return {
    key: `purchase-${purchase.id}`,
    kind: 'purchase',
    id: purchase.id,
    href: `/purchase-progress/${purchase.id}`,
    image: target.images?.top || target.images?.upper || placeholderFor(target).url,
    title: formatPedalTitle(target),
    cta: getPurchaseDashboardCta(purchase, uid),
    otherUid,
  };
};

/**
 * Horizontal strip of in-progress trades & purchases (logged-in dashboard only).
 * @param {(visible: boolean) => void} [onVisibilityChange] — when the strip is shown (incl. loading skeleton) vs hidden
 */
const ActiveTradesPurchasesStrip = ({ user, onVisibilityChange }) => {
  const [cards, setCards] = useState([]);
  const [usernames, setUsernames] = useState({});
  const [loading, setLoading] = useState(true);

  const tradeInitRef = useRef([]);
  const tradeRespRef = useRef([]);
  const purInitRef = useRef([]);
  const purRespRef = useRef([]);
  /** Per-logged-in-user username map; uid must match or cache is reset (remount / account switch). */
  const usernameCacheRef = useRef({ uid: null, names: {} });

  const mergeAndEnrich = useCallback(async () => {
    const uid = user?.uid;
    if (!uid) return;

    if (usernameCacheRef.current.uid !== uid) {
      usernameCacheRef.current = { uid, names: {} };
    }
    const nameByUid = usernameCacheRef.current.names;

    const tradeMap = new Map();
    for (const d of [...tradeInitRef.current, ...tradeRespRef.current]) {
      const data = d.data();
      if (TERMINAL_STATUSES.has(data.status)) continue;
      if (!tradeMap.has(d.id)) {
        tradeMap.set(d.id, { kind: 'trade', id: d.id, ...data });
      }
    }
    const purchaseMap = new Map();
    for (const d of [...purInitRef.current, ...purRespRef.current]) {
      const data = d.data();
      if (TERMINAL_STATUSES.has(data.status)) continue;
      if (!purchaseMap.has(d.id)) {
        purchaseMap.set(d.id, { kind: 'purchase', id: d.id, ...data });
      }
    }

    const merged = [...tradeMap.values(), ...purchaseMap.values()]
      .filter((row) => {
        if (row.kind === 'trade') return !tradeOfferFullyDone(row);
        return !purchaseOfferFullyDone(row);
      })
      .sort((a, b) => {
        const ta = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
        const tb = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
        return tb - ta;
      });

    const enriched = await Promise.all(
      merged.map((row) => (row.kind === 'trade' ? enrichTrade(row, uid) : enrichPurchase(row, uid)))
    );

    setCards(enriched);

    const ids = [...new Set(enriched.map((c) => c.otherUid).filter(Boolean))];
    const updates = {};
    const missing = ids.filter((ouid) => !nameByUid[ouid]);
    ids.forEach((ouid) => { if (nameByUid[ouid]) updates[ouid] = nameByUid[ouid]; });
    if (missing.length > 0) {
      // PII isolation: counterparty names from the public mirror (no email
      // leak). One documentId()-in batch per 30 instead of one getDoc each
      // (2026-10-31): the names are the last thing before the strip reports
      // ready, so on the phone's one-request-at-a-time transport every
      // extra read sat on that path.
      const profiles = await fetchPublicProfiles(missing, { fallback: { username: 'Member' } });
      missing.forEach((ouid) => {
        const name = (profiles[ouid] && profiles[ouid].username) || 'Member';
        nameByUid[ouid] = name;
        updates[ouid] = name;
      });
    }
    if (Object.keys(updates).length > 0) {
      setUsernames((prev) => ({ ...prev, ...updates }));
    }
    perfMark('pxc:active-strip-data');
    setLoading(false);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setCards([]);
      setUsernames({});
      setLoading(false);
      usernameCacheRef.current = { uid: null, names: {} };
      return;
    }

    setLoading(true);
    setUsernames({});
    usernameCacheRef.current = { uid: null, names: {} };
    const uid = user.uid;

    const tq1 = query(collection(db, 'tradeOffers'), where('status', 'in', TRADE_STATUSES), where('fromUserId', '==', uid));
    const tq2 = query(collection(db, 'tradeOffers'), where('status', 'in', TRADE_STATUSES), where('toUserId', '==', uid));
    const pq1 = query(collection(db, 'purchaseOffers'), where('status', 'in', PURCHASE_STATUSES), where('initiatorId', '==', uid));
    const pq2 = query(collection(db, 'purchaseOffers'), where('status', 'in', PURCHASE_STATUSES), where('responderId', '==', uid));

    // The first pass waits until all four listeners have delivered once, so
    // the strip is built one time from the complete set instead of up to
    // four times from partial sets (each pass re-ran the enrichment and could
    // paint a shorter list first). After that, every change recomputes.
    const delivered = new Set();
    const run = (which) => {
      delivered.add(which);
      if (delivered.size < 4) return;
      mergeAndEnrich();
    };
    const unsub = [
      onSnapshot(tq1, (snap) => { tradeInitRef.current = snap.docs; run('tq1'); }),
      onSnapshot(tq2, (snap) => { tradeRespRef.current = snap.docs; run('tq2'); }),
      onSnapshot(pq1, (snap) => { purInitRef.current = snap.docs; run('pq1'); }),
      onSnapshot(pq2, (snap) => { purRespRef.current = snap.docs; run('pq2'); }),
    ];

    return () => {
      unsub.forEach((u) => u());
      tradeInitRef.current = [];
      tradeRespRef.current = [];
      purInitRef.current = [];
      purRespRef.current = [];
      usernameCacheRef.current = { uid: null, names: {} };
    };
  }, [user?.uid, mergeAndEnrich]);

  useEffect(() => {
    if (!onVisibilityChange) return;
    const visible = !!(user?.uid && (loading || cards.length > 0));
    onVisibilityChange(visible);
  }, [user?.uid, loading, cards.length, onVisibilityChange]);

  if (!user?.uid || (!loading && cards.length === 0)) {
    return null;
  }

  return (
    <section className="active-trades-purchases-strip-shell">
      <div className="active-trades-purchases-strip">
      <h2
        className="active-trades-purchases-strip__title"
        style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: '#4b5563',
          margin: '0 0 0.75rem 0',
          fontFamily: 'Nunito, sans-serif',
        }}
      >
        ACTIVE TRADES & PURCHASES
      </h2>
      <div
        style={{
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          paddingBottom: '4px',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
        }}
      >
        {(loading && cards.length === 0 ? [1, 2] : cards).map((card, idx) =>
          loading && cards.length === 0 ? (
            // Placeholder built from the SAME boxes as a real card (image box
            // with the same aspect ratio and minimum height, then three text
            // rows at the card's font sizes), so replacing it with cards does
            // not change the strip's height and nothing below moves
            // (2026-10-31; the flat 280px block was 38px taller than a card).
            <div key={`sk-${idx}`} aria-hidden style={{ minWidth: '200px', maxWidth: '220px', flexShrink: 0, display: 'block' }}>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'relative', width: '100%', minHeight: '152px', aspectRatio: '16 / 11', background: '#e5e7eb', flexShrink: 0 }} />
                <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ fontSize: '0.75rem', margin: '0 0 6px 0', fontFamily: 'Nunito, sans-serif' }}>
                    <span style={{ display: 'inline-block', width: '55%', height: '0.75em', borderRadius: '4px', background: '#e5e7eb', verticalAlign: 'middle' }} />
                  </p>
                  <p style={{ fontSize: '0.9rem', margin: '0 0 8px 0', lineHeight: 1.25, minHeight: 'calc(0.9rem * 1.25 * 2)', fontFamily: 'Nunito, sans-serif' }}>
                    <span style={{ display: 'inline-block', width: '80%', height: '0.85em', borderRadius: '4px', background: '#e5e7eb', verticalAlign: 'middle' }} />
                  </p>
                  <span style={{ fontSize: '0.8rem', marginTop: 'auto' }}>
                    <span style={{ display: 'inline-block', width: '45%', height: '0.8em', borderRadius: '4px', background: '#e5e7eb', verticalAlign: 'middle' }} />
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <Link
              key={card.key}
              to={card.href}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                minWidth: '200px',
                maxWidth: '220px',
                flexShrink: 0,
                display: 'block',
              }}
            >
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: '#fff',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    minHeight: '152px',
                    aspectRatio: '16 / 11',
                    background: '#e8e4dc',
                    flexShrink: 0,
                  }}
                >
                  {card.image ? (
                    <ListingThumb
                      src={card.image}
                      alt=""
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#F7F3EB',
                      }}
                    >
                      <span style={{ fontSize: '2rem', opacity: 0.35 }} aria-hidden>
                        🎛
                      </span>
                    </div>
                  )}
                  {card.image && (
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.08) 45%, transparent 65%)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  <span
                    style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      zIndex: 2,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: '999px',
                      fontFamily: 'Nunito, sans-serif',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                      ...(card.kind === 'trade'
                        ? { background: '#dcfce7', color: '#166534' }
                        : { background: '#ffedd5', color: '#9a3412' }),
                    }}
                  >
                    {card.kind === 'trade' ? 'Trade' : 'Purchase'}
                  </span>
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      bottom: '10px',
                      transform: 'translateX(-50%)',
                      zIndex: 2,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#5A9ECB',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      maxWidth: 'calc(100% - 16px)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                    }}
                  >
                    {card.cta}
                  </div>
                </div>
                <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 6px 0', fontFamily: 'Nunito, sans-serif' }}>
                    from{' '}
                    <span style={{ color: '#374151', fontWeight: 600 }}>
                      {usernames[card.otherUid] || '…'}
                    </span>
                  </p>
                  <p
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#111827',
                      margin: '0 0 8px 0',
                      lineHeight: 1.25,
                      minHeight: 'calc(0.9rem * 1.25 * 2)',
                      fontFamily: 'Nunito, sans-serif',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {card.title}
                  </p>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#5A9ECB', marginTop: 'auto' }}>
                    View progress →
                  </span>
                </div>
              </div>
            </Link>
          )
        )}
      </div>
      </div>
    </section>
  );
};

export default ActiveTradesPurchasesStrip;
